<template>
  <div class="summary-card">
    <div class="icon-wrapper" :style="iconWrapperStyle">
      <i :class="['icon', icon]" :style="iconStyle"></i>
    </div>
    <div class="content-wrapper">
      <div class="summary-label">{{ title }}</div>
      <div class="summary-value">{{ formattedValue }}</div>
      <div v-if="variation !== undefined" class="summary-footer">
        <span class="variation-badge" :class="variationClass">
          {{ variation > 0 ? '+' : '' }}{{ (variation * 100).toFixed(1) }}%
        </span>
        <span class="period-text">vs previous period</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SummarySection',
  props: {
    title: {
      type: String,
      required: true
    },
    value: {
      type: [Number, String],
      required: true
    },
    variation: {
      type: Number,
      default: undefined
    },
    isCurrency: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: 'fas fa-chart-line'
    },
    iconColor: {
      type: String,
      default: '#4F46E5'
    }
  },
  computed: {
    formattedValue() {
      if (this.isCurrency) {
        return this.formatCurrency(this.value);
      }
      return this.value.toLocaleString();
    },
    variationClass() {
      return {
        positive: this.variation > 0,
        negative: this.variation < 0,
        neutral: this.variation === 0
      };
    },
    iconWrapperStyle() {
      return {
        backgroundColor: this.hexToRgba(this.iconColor, 0.15)
      };
    },
    iconStyle() {
      return {
        color: this.iconColor
      };
    }
  },
  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0
      }).format(value);
    },
    hexToRgba(hex, opacity) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${opacity})`
        : hex;
    }
  }
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/_variables' as *;

.summary-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  background: $color-bg-widget;
  border: 1px solid $color-gray-divider;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  padding: 20px;
  border-radius: 12px;

  .icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .icon {
      font-size: 20px;
    }
  }

  .content-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .summary-label {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      color: $color-gray-light-alt;
      line-height: 1.2;
    }

    .summary-value {
      font-size: 28px;
      font-weight: bold;
      color: $color-navy-primary-alt;
      line-height: 1.1;
    }

    .summary-footer {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 4px;

      .variation-badge {
        padding: 2px 8px;
        border-radius: 20px;
        font-size: 11px;
        font-weight: 600;
        line-height: 1.2;

        &.positive {
          background: $color-success-light;
          color: $color-success-dark;
        }

        &.negative {
          background: $color-error-light;
          color: $color-error-dark;
        }

        &.neutral {
          background: $color-neutral-light;
          color: $color-neutral-dark;
        }
      }

      .period-text {
        font-size: 11px;
        color: $color-gray-lighter;
        font-weight: 400;
      }
    }
  }
}
</style>
